import { withAITracking } from "@microsoft/applicationinsights-react-js";
import React from "react";
import { Helmet } from "react-helmet-async";
import { useHistory } from "react-router";

import { List } from "/component/base";
import * as Icons from "/component/base/Icon";
import { Props as IconProps } from "/component/base/Icon/Icon.types";
import InfoBanner from "/component/base/InfoBanner";
import Text from "/component/base/Text";
import MainContent from "/component/layout/MainContent";
import MainContentLoginCta from "/component/partial/MainContentLoginCta";
import { useDocumentsContext } from "/component/provider/DocumentsProvider/DocumentsProvider";
import routes from "/constant/url.constant";
import { useAuth, useInfoBanners, useTranslation } from "/hook";
import { reactPlugin } from "/util/appInsights.util";
import {
  hasFeatureFlagDisabled,
  hasFeatureFlagEnabled,
  hasFeatureFlagLegacy,
} from "/util/featureFlags/featureFlags";

import {
  BadgeContainer,
  IconContainer,
  ListContainer,
  MyHealthItem,
  TextContent,
} from "./MyHealthIndex.styles";

interface OptionProp {
  Icon: (prop: IconProps) => JSX.Element;
  title: string;
  to: string & {
    pathname: string;
  };
  hide?: boolean;
  badge?: number | null;
}

const MyHealthIndex = () => {
  const { t } = useTranslation("myHealth");
  const history = useHistory();
  const { isAuthenticated, login } = useAuth();
  const { enabled, showIcon, messageText, linkText, link, variant } = useInfoBanners("my_health");
  const {
    totalUnreadCount,
    loadingInOfficeDocs: loadingInOffice,
    loadingVideoDocs: loadingVideos,
  } = useDocumentsContext();

  //Not authorized show an empty state
  if (!isAuthenticated) {
    return (
      <MainContent pageTitle={t("title")}>
        <Helmet>
          <title>{t("title")}</title>
        </Helmet>

        <MainContentLoginCta
          title={t("empty.title")}
          subtitle={t("empty.subtitle")}
          login={() => login()}
        />
      </MainContent>
    );
  }

  const options = [
    {
      Icon: Icons.Clipboard,
      title: t("results"),
      to: routes.results,
      hide: hasFeatureFlagDisabled("test_results_enabled"),
    },
    {
      Icon: Icons.Medicine,
      title: t("medications", "Medications"),
      to: routes.medications,
      hide: !hasFeatureFlagEnabled("medications_enabled"),
    },
    {
      Icon: Icons.Medicine,
      title: t("prescriptions"),
      to: routes.prescriptions,
      hide: !hasFeatureFlagLegacy("medications_enabled"),
    },
    {
      Icon: Icons.PreferredPharmacy,
      title: t("preferredPharmacy"),
      to: routes.preferredPharmacy,
      hide: !hasFeatureFlagLegacy("medications_enabled"),
    },
    {
      Icon: Icons.FilePlus,
      title: t("careSummaries"),
      to: {
        pathname: routes.careSummaries,
      },
      hide: !hasFeatureFlagEnabled("care_summaries_enabled"),
    },
    {
      Icon: Icons.FileText,
      title: t("documents"),
      to: {
        pathname: routes.documents,
      },
      hide: hasFeatureFlagDisabled("documents_enabled"),
      badge: (loadingInOffice && loadingVideos) || totalUnreadCount === 0 ? null : totalUnreadCount,
    },
    {
      Icon: Icons.Vaccine,
      title: t("immunizations"),
      to: {
        pathname: routes.immunizations,
      },
      hide: !hasFeatureFlagEnabled("immunization_enabled"),
    },
  ].filter((o) => !o.hide);

  const filteredOptions = options.filter((el) => el) as OptionProp[];

  return (
    <MainContent pageTitle={t("headline")}>
      <Helmet>
        <title>{t("title")}</title>
      </Helmet>

      <InfoBanner
        enabled={enabled}
        showIcon={showIcon}
        linkText={linkText}
        link={link}
        variant={variant}
        css={{ marginBottom: 24 }}
      >
        <Text variant="body1">{messageText}</Text>
      </InfoBanner>

      <ListContainer>
        <List
          as="Buttons"
          hideLastChildBorder
          onClick={({ to }) => {
            history.push(to);
          }}
          data={filteredOptions}
          renderItem={({ Icon, title, badge }: OptionProp) => (
            <MyHealthItem>
              <IconContainer>
                <Icon
                  color="brandSecondary"
                  background={{ size: 54, color: "brandSecondaryLight" }}
                />
              </IconContainer>
              <TextContent>
                <Text variant="body1Bold">{title}</Text>
                {typeof badge === "number" && badge > 0 && (
                  <BadgeContainer>
                    <Text css={{ alignSelf: "center" }} variant="body1Bold" color="white">
                      {badge > 99 ? "99+" : badge}
                    </Text>
                  </BadgeContainer>
                )}
              </TextContent>
            </MyHealthItem>
          )}
        />
      </ListContainer>
    </MainContent>
  );
};

export default withAITracking(reactPlugin, MyHealthIndex, undefined, "ai-tracking");
